import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { FixaQuantityStepper } from "@coworker/apprestructured/src/shared/wrappers/FixaQuantityStepper/FixaQuantityStepper";
import { DefaultScannerComponent } from "@ingka-group-digital/flexa-scanner";
import { ScanAndSearchSlot } from "@coworker/apprestructured/src/scan-and-search/ScanAndSearchSlot";
import { useGetItemOrLocalCombination } from "@coworker/apprestructured/src/shared/hooks/item/useGetItemOrLocalCombination";
import { SelectedItemCard } from "./SelectedItemCard/SelectedItemCard";
import { LoadingArticle } from "./LoadingArticle/LoadingArticle";
import { MAX_QUANTITY_NUMBER } from "@coworker/app/src/components/InputPopup/AddMultipleArticles.helper";
import styles from "./AttachArticlesWithQuantityStepper.module.css";

export type ArticleWithQuantity = {
  item: Item;
  quantity: number;
};

type AttachArticlesWithQuantityStepperProps = {
  articles: ArticleWithQuantity[];
  setArticles: React.Dispatch<React.SetStateAction<ArticleWithQuantity[]>>;
  maxArticles: number;
  isActive: boolean;
  enableMultiSelectSearch?: boolean;
  preselectedItemNos?: string[];
  showQuantity?: boolean;
};

export const AttachArticlesWithQuantityStepper = ({
  articles,
  setArticles,
  maxArticles,
  isActive,
  enableMultiSelectSearch = false,
  preselectedItemNos = [],
  showQuantity = true,
}: AttachArticlesWithQuantityStepperProps) => {
  const { t } = useTranslation();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const getItemOrLocalCombination = useGetItemOrLocalCombination();

  const handleScannedArticle = useCallback(
    async (itemNo?: string) => {
      setIsCameraOpen(false);
      setShowToast(false);

      if (!itemNo || isLoading) return;

      const existing = articles.find((a) => a.item.no === itemNo);
      if (existing) {
        if (existing.quantity >= MAX_QUANTITY_NUMBER) return;
        setArticles((prev) =>
          prev.map((a) =>
            a.item.no === itemNo ? { ...a, quantity: a.quantity + 1 } : a
          )
        );
        return;
      }

      if (articles.length >= maxArticles) {
        setToastMessage("tooManyArticlesAddedString");
        setShowToast(true);
        return;
      }

      setIsLoading(true);
      try {
        const item = await getItemOrLocalCombination(itemNo);
        if (item) {
          setArticles((prev) => [...prev, { item, quantity: 1 }]);
        } else {
          setToastMessage("couldNotAddArticleString");
          setShowToast(true);
        }
      } catch {
        setToastMessage("couldNotAddArticleString");
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, articles, maxArticles, getItemOrLocalCombination, setArticles]
  );

  const handleQuantityChange = useCallback(
    (no: string, value: number | string) => {
      const numValue = typeof value === "string" ? parseInt(value, 10) : value;
      if (isNaN(numValue)) return;
      if (numValue < 1) {
        setArticles((prev) => prev.filter((a) => a.item.no !== no));
      } else {
        setArticles((prev) =>
          prev.map((a) => (a.item.no === no ? { ...a, quantity: numValue } : a))
        );
      }
    },
    [setArticles]
  );

  const handleToggleItemSelect = useCallback(
    (item: Item) => {
      const isPreselected = preselectedItemNos.includes(item.no);
      const alreadySelected = articles.some(
        (article) => article.item.no === item.no
      );

      if (isPreselected && !alreadySelected) {
        setToastMessage("articleAlreadyExistsString");
        setShowToast(true);
        return;
      }

      if (!alreadySelected && articles.length >= maxArticles) {
        setToastMessage("tooManyArticlesAddedString");
        setShowToast(true);
        return;
      }

      setShowToast(false);
      setArticles((prev) =>
        alreadySelected
          ? prev.filter((article) => article.item.no !== item.no)
          : [...prev, { item, quantity: 1 }]
      );
    },
    [articles, maxArticles, preselectedItemNos, setArticles]
  );

  const selectedItemsForSearch = useMemo(() => {
    const selectedNos = new Set<string>(preselectedItemNos);
    articles.forEach(({ item }) => {
      selectedNos.add(item.no);
    });

    return Array.from(selectedNos);
  }, [articles, preselectedItemNos]);

  const totalArticleCount = useMemo(
    () => articles.reduce((sum, a) => sum + a.quantity, 0),
    [articles]
  );

  const searchSlot = (
    <ScanAndSearchSlot
      onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
      onCameraOpen={() => setIsCameraOpen(true)}
      multiSelect={enableMultiSelectSearch}
      selectedItemNos={selectedItemsForSearch}
      onToggleItemSelect={handleToggleItemSelect}
    />
  );

  if (isCameraOpen) {
    return (
      <DefaultScannerComponent
        onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
        isCameraOpen={isCameraOpen}
        onCameraOpenChange={setIsCameraOpen}
        searchSlot={searchSlot}
      />
    );
  }

  return (
    <>
      {isActive && (
        <DefaultScannerComponent
          onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
          isCameraOpen={isCameraOpen}
          onCameraOpenChange={setIsCameraOpen}
          searchSlot={searchSlot}
        />
      )}

      <div>
        {articles.length > 0 && (
          <div className={styles["articleCountHeader"]}>
            <FixaText textStyle="Heading.XS">
              {articles.length === 1
                ? `1 ${t("articleSelectedString")}`
                : t("articlesSelectedCountString", {
                    count: articles.length,
                  })}{" "}
              {showQuantity && (
                <>
                  ({totalArticleCount}{" "}
                  {t("totalString", { defaultValue: "total" })})
                </>
              )}
            </FixaText>
          </div>
        )}

        <div className={styles["articleList"]}>
          {articles.map((article) => (
            <div className={styles["articleRow"]} key={article.item.no}>
              <div className={styles["articleCardWrapper"]}>
                <SelectedItemCard
                  item={article.item}
                  setSearchHistory={() => {}}
                  searchHistory={[]}
                  handleProductClick={() => {}}
                />
              </div>
              {showQuantity && (
                <div className={styles["stepperWrapper"]}>
                  <FixaQuantityStepper
                    small
                    minVal={0}
                    maxVal={MAX_QUANTITY_NUMBER}
                    value={article.quantity}
                    ariaDescribedBy={`Quantity for ${article.item.name}`}
                    ariaDescribedById={`qty-${article.item.no}`}
                    ariaLabelInput={t("quantityString", {
                      defaultValue: "Quantity",
                    })}
                    ariaLabelIncrease={t("increaseQuantityString", {
                      defaultValue: "Increase",
                    })}
                    ariaLabelDecrease={t("decreaseQuantityString", {
                      defaultValue: "Decrease",
                    })}
                    onIncrease={() =>
                      handleQuantityChange(
                        article.item.no,
                        article.quantity + 1
                      )
                    }
                    onDecrease={() =>
                      handleQuantityChange(
                        article.item.no,
                        article.quantity - 1
                      )
                    }
                    onQuantityChange={(value) =>
                      handleQuantityChange(article.item.no, value)
                    }
                  />
                </div>
              )}
            </div>
          ))}
          {isLoading && <LoadingArticle />}
        </div>
      </div>

      <FixaToast
        isOpen={showToast}
        text={toastMessage ? t(toastMessage) : ""}
        onCloseRequest={() => setShowToast(false)}
      />
    </>
  );
};
